import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'

import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { formatMoney, formatPercentage } from '../../../packages/utils/numberFormatters'
import InfoBlock from '../../components/InfoBlock'
import { InvestorConvertible } from 'types/InvestorEntity'

const compareBySizeAndName = (a: InvestorConvertible, b: InvestorConvertible) => {
    const aSize = a.amountInCash || 0
    const bSize = b.amountInCash || 0
    if (aSize > bSize) {
        return -1
    }
    if (aSize < bSize) {
        return 1
    }
    if (a.name < b.name) {
        return -1
    }
    if (a.name > b.name) {
        return 1
    }
    return 0
}

const INCOMPLETE_INFORMATION_TEXT =
    'Fullständig information angående investerare och vilka belopp har ej kommunicerats av bolaget.'

function ConvertibleInvestors({ convertible }: { convertible: ConvertibleEntity }) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const { investors } = convertible
    let content = null

    if (investors.length > 0) {
        const totalInvestorsSize = investors.reduce((acc, row) => acc + (row.amountInCash || 0), 0) || null
        const totalInvestorsPercentage = investors.reduce((acc, row) => acc + (row.amountInPercentage || 0), 0) || null

        content = (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Namn</TableCell>
                            <TableCell>Belopp ({convertible.currency})</TableCell>
                            <TableCell>Andel</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {investors.sort(compareBySizeAndName).map((investor) => {
                            return (
                                <TableRow key={investor.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell>
                                        {investor.name === '?' ? (
                                            <Typography variant='body3'>Ej namngiven</Typography>
                                        ) : (
                                            <Link to={`/investors/${investor.investorId}`}>{investor.name}</Link>
                                        )}
                                    </TableCell>
                                    <TableCell component='td' scope='row'>
                                        {investor.amountInCash !== null
                                            ? formatMoney(investor.amountInCash, {
                                                  locale,
                                                  decimals: 0,
                                              })
                                            : '*'}
                                    </TableCell>
                                    <TableCell component='td' scope='row'>
                                        {investor.amountInCash !== null
                                            ? formatPercentage(investor.amountInPercentage, {
                                                  locale,
                                                  decimals: 1,
                                              })
                                            : '*'}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        <TableRow>
                            <TableCell>
                                <b>Summa</b>
                            </TableCell>
                            <TableCell>
                                <b>{formatMoney(totalInvestorsSize, { locale, decimals: 0 })}</b>
                            </TableCell>
                            <TableCell>
                                <b>
                                    {totalInvestorsPercentage
                                        ? formatPercentage(totalInvestorsPercentage, {
                                              locale,
                                              minDecimals: 0,
                                              maxDecimals: 1,
                                          })
                                        : ''}
                                </b>
                            </TableCell>
                        </TableRow>
                        {totalInvestorsPercentage === null ||
                            (totalInvestorsPercentage < 99 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <i>{INCOMPLETE_INFORMATION_TEXT}</i>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    } else {
        content = (
            <span>
                <i>{INCOMPLETE_INFORMATION_TEXT}</i>
            </span>
        )
    }

    return (
        <InfoBlock title='Investerare' loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}

export default ConvertibleInvestors
